import ApplicationController from "./application_controller"
import Cookies from 'js-cookie'
import {
  applySavedOttomanSideKeyboardDragPosition,
  deleteCharBeforeCaret,
  hideOttomanSideKeyboardPanel,
  isOttomanSideKeyboardOpen,
  showOttomanSideKeyboardPanel,
  syncOttomanSideKeyboardAriaHidden
} from "./lexiqamus_search_bar_controller"
import {
  attachLexiqamusOttomanKeyboardDrag,
  TYPEO_OTTOMAN_KEYBOARD_DRAG_KEY
} from "./lexiqamus_search/ottoman_keyboard"

// Type-L uses the legacy flat keyboard only. TypeO adds the Lexiqamus grouped palette alongside it.
export default class extends ApplicationController {

  static targets = [
    'onscreenKeyboard',
    'ottomanSideKeyboard',
    'ottomanKeyboardDragHandle'
  ]

  static values = {
    // Turkish locale maps event.key; English maps physical key codes (see resolveOttomanCharFromKeydown).
    isTurkish: Boolean
  }

  initialize() {
    window['onscreen_keyboard'] = this
    this.stage = this.element.dataset.stage
    this.addListnerToOnscreenKeyboardButtons()
  }

  connect() {
    if (this.isTypeoStage()) {
      this.setupTypeoLexiqamusKeyboard()
      if (this.hasOnscreenKeyboardTarget) {
        this.dragKeyboard()
        this.handleOnscreenKeyboardVisibility()
      }
      return
    }

    if (this.hasOnscreenKeyboardTarget) {
      this.dragKeyboard()
      this.handleOnscreenKeyboardVisibility()
    }
  }

  disconnect() {
    if (this.isTypeoStage()) {
      document.removeEventListener("mousedown", this.onOttomanKeyboardDocumentMouseDown)
      if (this.removeOttomanKeyboardDrag) {
        this.removeOttomanKeyboardDrag()
        this.removeOttomanKeyboardDrag = null
      }
    }
  }

  setupTypeoLexiqamusKeyboard() {
    // Lexiqamus grouped palette: drag, cookie restore, and click-outside dismiss.
    this.onOttomanKeyboardDocumentMouseDown = this.onOttomanKeyboardDocumentMouseDown.bind(this)
    document.addEventListener("mousedown", this.onOttomanKeyboardDocumentMouseDown)
    this.removeOttomanKeyboardDrag = null
    if (this.hasOttomanSideKeyboardTarget && this.hasOttomanKeyboardDragHandleTarget) {
      this.removeOttomanKeyboardDrag = attachLexiqamusOttomanKeyboardDrag(
        this.ottomanSideKeyboardTarget,
        this.ottomanKeyboardDragHandleTarget,
        TYPEO_OTTOMAN_KEYBOARD_DRAG_KEY
      )
    }
    if (this.hasOttomanSideKeyboardTarget) {
      syncOttomanSideKeyboardAriaHidden(this.ottomanSideKeyboardTarget)
    }
    this.handleTypeoLexiqamusKeyboardVisibility()
  }

  isTypeoStage() {
    return this.stage === 'type_o'
  }

  get typingFormController() {
    return this.application.getControllerForElementAndIdentifier(this.element, "typing-form")
  }

  keyboardVisibilityCookie() {
    return `onscreen_keyboard_for_${this.stage}_stage`
  }

  lexiqamusKeyboardVisibilityCookie() {
    return `lexiqamus_ottoman_keyboard_for_${this.stage}_stage`
  }

  handleOnscreenKeyboardVisibility() {
    if (!this.hasOnscreenKeyboardTarget) return
    // Read the visibility status from the cookie
    const keyboardVisible = Cookies.get(this.keyboardVisibilityCookie()) === 'true'
    this.onscreenKeyboardTarget.classList.toggle('collapse', !keyboardVisible)
  }

  handleTypeoLexiqamusKeyboardVisibility() {
    if (!this.hasOttomanSideKeyboardTarget) return
    const keyboardVisible = Cookies.get(this.lexiqamusKeyboardVisibilityCookie()) === 'true'
    if (keyboardVisible) {
      this.showOttomanSideKeyboard()
    } else {
      this.hideOttomanSideKeyboard()
    }
  }

  toggleOnscreenKeyboardVisibility(e) {
    if (!this.hasOnscreenKeyboardTarget) return
    this.onscreenKeyboardTarget.classList.toggle("collapse")
    const isVisible = !this.onscreenKeyboardTarget.classList.contains("collapse")
    Cookies.set(this.keyboardVisibilityCookie(), isVisible)
  }

  toggleLexiqamusOttomanKeyboardVisibility(e) {
    if (!this.isTypeoStage() || !this.hasOttomanSideKeyboardTarget) return
    if (this.isOttomanKeyboardOpen()) {
      this.hideOttomanSideKeyboard()
      Cookies.set(this.lexiqamusKeyboardVisibilityCookie(), false)
    } else {
      this.showOttomanSideKeyboard()
      Cookies.set(this.lexiqamusKeyboardVisibilityCookie(), true)
    }
  }

  isOttomanKeyboardOpen() {
    return this.hasOttomanSideKeyboardTarget &&
      isOttomanSideKeyboardOpen(this.ottomanSideKeyboardTarget)
  }

  showOttomanSideKeyboard() {
    if (!this.hasOttomanSideKeyboardTarget) return
    // TypeO has no search-bar wrapper — restore drag offset or fall back to SCSS bottom-right default.
    showOttomanSideKeyboardPanel(this.ottomanSideKeyboardTarget, {
      afterOpen: () => applySavedOttomanSideKeyboardDragPosition(
        this.ottomanSideKeyboardTarget,
        TYPEO_OTTOMAN_KEYBOARD_DRAG_KEY
      )
    })
  }

  hideOttomanSideKeyboard() {
    if (!this.hasOttomanSideKeyboardTarget) return
    hideOttomanSideKeyboardPanel(this.ottomanSideKeyboardTarget)
  }

  onOttomanKeyboardDocumentMouseDown(event) {
    if (!this.isOttomanKeyboardOpen()) return
    if (this.hasOttomanSideKeyboardTarget && this.ottomanSideKeyboardTarget.contains(event.target)) return
    const input = event.target.closest?.("input")
    // Keep open while the user clicks any Ottoman typing field on the word-edit form.
    if (input && this.typingFormController?.isOttomanTypingField(input)) return
    this.hideOttomanSideKeyboard()
    Cookies.set(this.lexiqamusKeyboardVisibilityCookie(), false)
  }

  ottomanKeyboardKeyMouseDown(event) {
    event.preventDefault()
    const ch = event.currentTarget.getAttribute("data-mapped-value")
    if (!ch) return
    const tf = this.typingFormController
    const field = tf?.activeOttomanField()
    if (!field || !tf) return
    tf.lastFocusedField = field
    tf.addCharacterWhereCursorPointing(ch)
  }

  // On-screen backspace — inserts via typing-form's focused Ottoman field, not search input.
  ottomanKeyboardBackspaceMouseDown(event) {
    event.preventDefault()
    deleteCharBeforeCaret(this.typingFormController?.activeOttomanField())
  }

  ottomanKeyboardCloseMouseDown(event) {
    event.preventDefault()
    this.hideOttomanSideKeyboard()
    Cookies.set(this.lexiqamusKeyboardVisibilityCookie(), false)
    this.typingFormController?.activeOttomanField()?.focus()
  }

  addListnerToOnscreenKeyboardButtons() {
    const buttons = document.querySelectorAll(".js-onscreen-keyboard-btns");
    const action = "click->typing-form#addSpecialCharacterForTypingForm";

    buttons.forEach(button => {
      button.setAttribute("data-action", action);
    });
  }

  dragKeyboard() {
    dragKeyboardElement(this.onscreenKeyboardTarget);
  }
}

const dragKeyboardElement = (elmnt) => {
  var pos1 = 0, pos2 = 0, pos3 = 0, pos4 = 0;

  var saveMouseUp = null;
  var saveMouseMove = null;

  if (elmnt) {
    const lastPosition = localStorage.getItem("keyboardLastPositionOnScreen");
    if (lastPosition) {
      const { top, left } = JSON.parse(lastPosition);
      elmnt.style.top = top;
      elmnt.style.left = left;
    }

    elmnt.onmousedown = dragMouseDown;
  }

  function dragMouseDown(e) {
    e = e || window.event;
    e.preventDefault();
    // get the mouse cursor position at startup:
    pos3 = e.clientX;
    pos4 = e.clientY;
    saveMouseUp = document.onmouseup;
    saveMouseMove = document.onmousemove;

    document.onmouseup = closeDragElement;
    document.onmousemove = elementDrag;
  }

  function elementDrag(e) {
    e = e || window.event;
    e.preventDefault();
    // calculate the new cursor position:
    pos1 = pos3 - e.clientX;
    pos2 = pos4 - e.clientY;
    pos3 = e.clientX;
    pos4 = e.clientY;
    // set the element's new position:
    elmnt.style.top = (elmnt.offsetTop - pos2) + "px";
    elmnt.style.left = (elmnt.offsetLeft - pos1) + "px";
  }

  function closeDragElement() {
    // stop moving when mouse button is released:
    document.onmouseup = saveMouseUp;
    document.onmousemove = saveMouseMove;

    // Save the current position to localStorage
    localStorage.setItem("keyboardLastPositionOnScreen", JSON.stringify({
      top: elmnt.style.top,
      left: elmnt.style.left
    }));
  }
}
